import { PLAIN_TEXT } from '@atlassian/jira-issue-shared-types/src/common/types/field-schema-type.tsx';
import { CMDB_OBJECT_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { FieldTransformer } from '../types/transformer-type.tsx';
import type { SomeJiraIssueField } from '../types/fields-type.tsx';
import {
	transformAggNodeToLegacyField,
	transformAggConnection,
} from './default-node-transformer.tsx';

// @ts-expect-error TODO JPX-157
const transformObjectTypeToLegacy = (attribute, workspaceId) => {
	if (!attribute) {
		return null;
	}
	return {
		workspaceId,
		id: `${workspaceId}:${attribute.objectTypeId}`,
		name: attribute.name,
		objectSchemaId: attribute.objectSchemaId,
		objectTypeId: attribute.objectTypeId,
		// `icon` is unused
		icon: attribute.icon,
	};
};

const transformCMDBConfigurationToLegacyConfiguration = (
	node: Pick<
		SomeJiraIssueField,
		'cmdbFieldConfig' | 'isInsightAvailable' | 'cmdbFieldConfig' | 'wasInsightRequestSuccessful'
	>,
) => {
	if (!node.cmdbFieldConfig) {
		return node.isInsightAvailable === false ? { isInsightAvailable: node.isInsightAvailable } : {};
	}

	return {
		cmdbFieldConfig: {
			multiple: node.cmdbFieldConfig.multiple,
			objectSchemaId: node.cmdbFieldConfig.objectSchemaId.split('/')[2],
			attributesDisplayedOnIssue: transformAggConnection(
				node.cmdbFieldConfig?.attributesDisplayedOnIssue,
			),
			attributesIncludedInAutoCompleteSearch: transformAggConnection(
				node.cmdbFieldConfig?.attributesIncludedInAutoCompleteSearch,
			),
		},
		isInsightAvailable: node.isInsightAvailable,
		wasInsightRequestSuccessful: node.wasInsightRequestSuccessful,
	};
};

// @ts-expect-error TODO JPX-157
const transformObjectTypeAttributeToLegacy = (type) => {
	switch (type) {
		case 'REFERENCED_OBJECT':
			return 1;
		case 'USER':
			return 2;
		case 'GROUP':
			return 4;
		case 'PROJECT':
			return 6;
		case 'STATUS':
			return 7;
		case 'BITBUCKET_REPO':
			return 8;
		case 'OPSGENIE_TEAM':
			return 9;
		case 'DEFAULT':
			return 0;
		default:
			// eslint-disable-next-line no-console
			console.error(
				new Error(
					`Unknown attribute type found in transformObjectTypeAttributeToLegacy: ${type}. Mapping type to '-1'`,
				),
			);
			return -1;
	}
};

// @ts-expect-error TODO JPX-157
const transformReferencedObject = (node) => ({
	id: node.objectGlobalId,
	workspaceId: node.workspaceId,
	objectKey: node.objectKey,
	label: node.label,
	avatar: node.avatar,
	objectId: node.objectId,
});

// @ts-expect-error TODO JPX-157
const transformCmdbUser = (node) => ({
	avatarUrl: node.picture,
	displayName: node.name,
	name: node.name,
	key: node.accountId,
});

// @ts-expect-error TODO JPX-157
const transformCmdbGroup = (node) => ({
	name: node.name,
});

// @ts-expect-error TODO JPX-157
const transformCmdbProject = (node) => ({
	avatarUrl: node.avatar?.large,
	id: Number(node.id),
	key: node.key,
	name: node.name,
});

// @ts-expect-error TODO JPX-157
const transformCmdbBitbucketRepo = (node) => ({
	uuid: node.uuid,
	name: node.name,
	url: node.url,
	avatarUrl: node.avatarUrl,
});

// @ts-expect-error TODO JPX-157
const transformCmdbOpsgenieTeam = (node) => ({
	id: node.id,
	name: node.name,
	url: node.url,
});

// @ts-expect-error TODO JPX-157
const transformCmdbStatus = (node) => ({
	id: node.id,
	name: node.name,
	category: node.category,
});

// @ts-expect-error TODO JPX-157
const transformObjectAttributeValuesToLegacy = (node) => {
	if (!node) {
		return null;
	}

	const defaultObjectValue = {
		displayValue: node.displayValue,
		searchValue: node.searchValue,
		...(node.value != null ? { value: node.value } : {}),
	};

	if (node.referencedObject) {
		return {
			...defaultObjectValue,
			referencedObject: transformReferencedObject(node.referencedObject),
		};
	}

	if (node.user) {
		return {
			...defaultObjectValue,
			user: transformCmdbUser(node.user),
		};
	}

	if (node.group) {
		return {
			...defaultObjectValue,
			group: transformCmdbGroup(node.group),
		};
	}

	if (node.project) {
		return {
			...defaultObjectValue,
			project: transformCmdbProject(node.project),
		};
	}

	if (node.bitbucketRepo) {
		return {
			...defaultObjectValue,
			bitbucketRepo: transformCmdbBitbucketRepo(node.bitbucketRepo),
		};
	}

	if (node.opsgenieTeam) {
		return {
			...defaultObjectValue,
			opsgenieTeam: transformCmdbOpsgenieTeam(node.opsgenieTeam),
		};
	}

	if (node.status) {
		return {
			...defaultObjectValue,
			status: transformCmdbStatus(node.status),
		};
	}

	return defaultObjectValue;
};

// @ts-expect-error TODO JPX-157
export const transformObjectAttributesToLegacyObjectAttributes = (node, workspaceId) => {
	if (!node) {
		return null;
	}

	const { objectTypeAttributeId } = node;

	const objectTypeAttribute = node.objectTypeAttribute;

	return {
		objectTypeAttributeId,
		workspaceId,
		objectAttributeValues: transformAggConnection(
			node.objectAttributeValues,
			transformObjectAttributeValuesToLegacy,
		),
		objectTypeAttribute: {
			id: objectTypeAttributeId,
			label: objectTypeAttribute.label,
			name: objectTypeAttribute.name,
			workspaceId,
			type: transformObjectTypeAttributeToLegacy(objectTypeAttribute.type),
			...(objectTypeAttribute.defaultType
				? {
						defaultType: {
							id: objectTypeAttribute.defaultType.id,
							name: objectTypeAttribute.defaultType.name,
						},
					}
				: {}),
		},
	};
};

// @ts-expect-error TODO JPX-157
const transformJiraCMDBToLegacyCMDB = (node) => {
	if (!node) {
		return null;
	}

	const { label, objectGlobalId, objectId, objectKey, webUrl, avatar } = node;

	const workspaceId = node.workspaceId;

	return {
		attributes: transformAggConnection(node.attributes, (nodeAttribute) =>
			transformObjectAttributesToLegacyObjectAttributes(nodeAttribute, workspaceId),
		),
		avatar,
		label,
		id: objectGlobalId,
		objectId,
		objectKey,
		workspaceId,
		links: {
			self: webUrl,
		},
		objectType: transformObjectTypeToLegacy(node.objectType, workspaceId),
	};
};

export const transformAggCMDBToLegacyField: FieldTransformer<typeof CMDB_OBJECT_CF_TYPE> = (
	node,
) => {
	if (node.type !== CMDB_OBJECT_CF_TYPE) return undefined;

	return {
		...transformAggNodeToLegacyField(node),
		autoCompleteUrl: node.searchUrl != null ? `${node.searchUrl}?` : null,
		configuration: transformCMDBConfigurationToLegacyConfiguration(node),
		value: transformAggConnection(
			node.selectedCmdbObjectsConnection,
			transformJiraCMDBToLegacyCMDB,
		),
		schema: {
			configuration: null,
			custom: CMDB_OBJECT_CF_TYPE,
			items: 'cmdb-object-field',
			renderer: PLAIN_TEXT,
			system: null,
			type: 'array',
		},
	};
};
